import { COMMA, ENTER } from "@angular/cdk/keycodes";
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { MatAutocompleteSelectedEvent } from "@angular/material/autocomplete";
import { MatChipInputEvent } from "@angular/material/chips";
import {
  EmployeeService,
  Pagination,
} from "blg-akaun-ts-lib";
import { AppConfig } from "../../visa";
import { Observable } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  map
} from "rxjs/operators";
import { SubSink } from "subsink2";

@Component({
  selector: 'app-sales-agent-search',
  templateUrl: './sales-agent.component.html',
  styleUrls: ['./sales-agent.component.css']
})
export class SalesAgentSearchComponent implements OnInit, OnDestroy {

  @Input() salesAgentControl = new FormControl();
  @Output() selectionChange = new EventEmitter();
  @Input() viewModelStore;
  @Input() ColumnViewActions;
  @Input() ColumnViewSelectors;
  @Input() label:string;

  private subs = new SubSink();
  separatorKeysCodes: number[] = [ENTER, COMMA];
  filteredSalesAgents$: Observable<any[]>;
  salesAgents: any[] = [];
  toppingsControl = new FormControl([]);

  @ViewChild("salesAgentInput") salesAgentInput: ElementRef<HTMLInputElement>;

  constructor(
    private employeeService: EmployeeService,
  ) {}

  ngOnInit(): void {
    this.viewModelStore
      .select(this.ColumnViewSelectors.selectAdvanceSearch_SalesAgent_Field)
      .subscribe((data) => {
        this.salesAgents = data;
      });

    this.salesAgentControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((data) => this.filterSalesAgent(data));
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || "").trim();
    // Add our sales agent
    if (value) {
      this.salesAgents.push(value);
    }

    // Clear the input value
    event.input.value = "";

    this.salesAgentControl.setValue(null);

    this.viewModelStore.dispatch(
      this.ColumnViewActions.setAdvanceSearch_SalesAgentField({
        salesAgents: this.salesAgents,
      })
    );
  }

  remove(salesAgent: string): void {
    const index = this.salesAgents.indexOf(salesAgent);

    if (index >= 0) {
      this.salesAgents.splice(index, 1);
    }
    this.viewModelStore.dispatch(
      this.ColumnViewActions.setAdvanceSearch_SalesAgentField({
        salesAgents: this.salesAgents,
      })
    );
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    console.log(event.option);
    this.salesAgents.push(event.option.value);
    this.salesAgentInput.nativeElement.value = "";
    this.salesAgentControl.setValue(null);
  }

  /**
   * Sales-agent (employee) autocomplete for the advanced-search chip list.
   *
   * Replaces the raw SELECT that went through SubQueryService:
   *
   *   FROM bl_fi_mst_entity_hdr hdr
   *   WHERE hdr.name ILIKE '%x%'
   *     AND hdr.status = 'ACTIVE'
   *     AND hdr.is_employee = true
   *   LIMIT 10
   *
   * EmployeeService points at entity/employees, and EntityController sets
   * is_employee = true from that path segment, so the is_employee scoping comes
   * from the endpoint. EntityUow.whereClauseCriteriaForSnapshot then applies
   * `search_word` via EntityUowHelper.generateSearchWordCondition and `status` via
   * EntityUowHelper.whereClauseHdr. The LIMIT 10 becomes the pagination limit.
   *
   * Behaviour note: with is_employee set, `search_word` ILIKEs employee_code,
   * job_title, department and the other employee columns alongside name, so the
   * autocomplete matches more than the old name-only filter.
   */
  filterSalesAgent(search: string) {
    this.filteredSalesAgents$ = this.employeeService
      .getByCriteria(
        new Pagination(0, 10, [
          { columnName: 'status', operator: '=', value: 'ACTIVE' },
          { columnName: 'search_word', operator: '=', value: (search || '').trim() }
        ]),
        AppConfig.apiVisa
      )
      .pipe(
        map((response) =>
          response.data.map((container: any) => ({
            guid: container.bl_fi_mst_entity_hdr.guid,
            code: container.bl_fi_mst_entity_hdr.employee_code,
            name: this.truncate(container.bl_fi_mst_entity_hdr.name, 3),
          }))
        )
      );
  }

  optionClicked(event: Event, salesAgent: any) {
    event.stopPropagation();
    this.toggleSelection(salesAgent);
  }

  truncate(str, no_words) {
    return str.split(" ").splice(0, no_words).join(" ");
  }

  toggleSelection(salesAgent: any) {
    console.log(salesAgent);
    salesAgent.selected = !salesAgent.selected;
    this.salesAgentInput.nativeElement.value = "";
    if (salesAgent.selected) {
      this.salesAgents.push(salesAgent);
    } else {
      const i = this.salesAgents.findIndex(value => value.guid === salesAgent.guid);
      this.salesAgents.splice(i, 1);
    }
  }

  onReset() {
    this.salesAgents = [];
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

}
